<template>
  <div class="tw-relative tw-w-full tw-h-full tw-flex tw-justify-center">
    <div class="player-container tw-w-full tw-h-full">
      <div
        :class="[
          'tw-relative video-wrapper',
          props.customVideoPlayer,
          props.width,
          props.height,
        ]"
      >
        <div v-if="!hasBeenPlay" class="tw-relative">
          <img
            :src="`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`"
            :alt="props.id"
            class="tw-w-full tw-h-full tw-object-cover"
            :class="[props.height]"
          />
        </div>
        <div
          :id="playerId"
          :class="[
            'video-player',
            props.width,
            props.height,
          ]"
        />
        <div
          :id="`${props.id}-box`"
          class="tw-absolute tw-top-0 tw-left-0 tw-bg-transparent tw-pointer-events-none"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import YTPlayer from 'yt-player'

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  url: {
    type: String,
    default: '',
  },
  width: {
    type: String,
    default: 'tw-w-343 md:tw-w-640',
  },
  height: {
    type: String,
    default: 'tw-h-48 md:tw-h-96',
  },
  customVideoPlayer: {
    type: String,
    default: ''
  },
  immediate: {
    type: Boolean,
    default: false,
  },
})

const { getYoutubeIdFromUrl } = useYoutube()

const playerId = ref('')
const innerValue = ref<boolean>(false)
const hasBeenPlay = ref<boolean>(false)
const youtubeId = ref<string | null>('')

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const player: any = ref()
let timer: ReturnType<typeof setTimeout>

onMounted(async () => {
  youtubeId.value = await getYoutubeIdFromUrl(props.url)
  if (props.immediate) {
    playBtn()
  } else {
    await autoPlaySetting()
  }
})

watch(hasBeenPlay, (val) => {
  if (val) {
    setTimeout(() => {
      const playerElement: HTMLElement | null = document.getElementById(
        playerId.value,
      )
      setupPlayer(playerElement)
    })
  }
})

const IntersectionEventTrigger = (entries: IntersectionObserverEntry[]) => {
  if (entries[0].isIntersecting) {
    if (!hasBeenPlay.value) {
      timer = setTimeout(() => {
        playBtn()
      }, 1000)
    } else {
      player.value.play()
    }
  } else {
    if (timer) {
      clearTimeout(timer)
    }
    if (player.value) {
      player.value.pause()
    }
  }
}

const autoPlaySetting = () => {
  const boxElement: Element | null = document.querySelector(`#${props.id}-box`)

  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 1.0,
  }
  const observer: IntersectionObserver = new IntersectionObserver(
    IntersectionEventTrigger,
    options,
  )
  boxElement && observer.observe(boxElement)
}

const playBtn = () => {
  playerId.value = props.id
  if (!hasBeenPlay.value) {
    hasBeenPlay.value = true
  }
}

const setupPlayer = (playerElement: HTMLElement | null) => {
  if (playerElement) {
    player.value = new YTPlayer(playerElement, {
      controls: true,
      keyboard: false,
      fullscreen: true,
      captions: false,
      related: false,
      host: 'https://www.youtube-nocookie.com',
    })
    player.value.load(youtubeId.value)
    setTimeout(() => {
      player.value.play()
      innerValue.value = true
      player.value.mute()
    }, 1500)
  }
}
</script>

<style>
.video-wrapper {
  aspect-ratio: 16 / 9;
  overflow: hidden;
}

.video-wrapper iframe {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
}
</style>
